import { Injectable } from '@angular/core';
import { LicenseService } from '../../license-list/services/license.service';
import {
  LicenseActiveGroupItem,
  LicenseActiveGroupResponse,
} from '../../license-list/models/license-active-group.model';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';

@Injectable({
  providedIn: 'root',
})
export class RecentActivityService {
  private activeGroupEntries: LicenseActiveGroupItem[] = [];
  private activeGroupIds = new Set<string>();

  constructor(
    private readonly licenseService: LicenseService,
    private readonly localSettingsService: LocalSettingsService,
  ) {}

  public async refreshActiveGroups(): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;

    if (!token) {
      this.activeGroupEntries = [];
      this.activeGroupIds = new Set<string>();
      return;
    }

    try {
      const activeGroups: LicenseActiveGroupResponse =
        await this.licenseService.loadActiveGroups(token);
      this.activeGroupEntries = activeGroups.active_groups ?? [];
      this.activeGroupIds = new Set(
        this.activeGroupEntries.map(({ group }) => group),
      );
    } catch {
      this.activeGroupEntries = [];
      this.activeGroupIds = new Set<string>();
    }
  }

  public isActiveGroup(groupId: string): boolean {
    return this.activeGroupIds.has(groupId);
  }

  public findMatchingGroupIds(searchTerm: string): Array<string> {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return [];
    }

    return [
      ...new Set(
        this.activeGroupEntries
          .filter((entry: LicenseActiveGroupItem) =>
            this.entryMatchesSearch(entry, normalizedSearchTerm),
          )
          .map(({ group }: LicenseActiveGroupItem) => group),
      ),
    ];
  }

  private entryMatchesSearch(
    entry: LicenseActiveGroupItem,
    searchTerm: string,
  ): boolean {
    const normalizedEmails = (entry.emails ?? []).map((email: string) =>
      email.toLowerCase(),
    );
    const normalizedNihii = (entry.nihii ?? []).map((nihii: string) =>
      nihii.toLowerCase(),
    );

    return (
      normalizedEmails.some((email: string) => email.includes(searchTerm)) ||
      normalizedNihii.some((nihii: string) => nihii.includes(searchTerm))
    );
  }
}
